import React, { useRef, useState, type PropsWithChildren } from "react"
import { SearchOverlayContext } from "./context"

interface Props {
  enabled: boolean
}

export const SearchOverlay: React.FC<PropsWithChildren<Props>> = ({
  children,
  enabled,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const showOverlay = enabled && isOpen
  const scrollTo = useRef(0)

  /**
   * we're creating a full-screen modal. Thats mostly handled in the CSS. but we
   * don't want it to mess with the the underlying scroll. So we track the
   * existing scroll location, turn off scrolling & move to the top. Then we
   * undo that when we close.
   */
  const setShowOverlay = (state: boolean) => {
    if (!enabled) return
    const inFrame = window !== window.top

    if (state && isOpen === false) {
      scrollTo.current = window.scrollY
      document.body.style.overflow = "hidden"
      document.body.style.backgroundColor = "rgba(0,0,0,.1)"
      window.scrollTo({ top: 0 })

      // We're in a frame, hard set the iframe body element to the modal max
      // height or the current document height. Whichever is smaller.
      // When turning off the overlay we unset this.
      if (inFrame) {
        // calc(100vh - 80px) is the modal css max height
        // It would be nice to be able to do this entirely in css
        // but css lengths fall down a bit inside frames.
        const modalMax = window.screen.availHeight - 80
        const currentHeight = document.body.clientHeight

        document.body.style.height = `${Math.min(currentHeight, modalMax)}px`
      }

      if (containerRef.current) {
        setTimeout(() => {
          containerRef.current.scrollIntoView()
        }, 0)
      }
    }

    if (!state) {
      document.body.style.overflow = "unset"
      document.body.style.backgroundColor = "unset"
      window.scrollTo({ top: scrollTo.current })
      if (inFrame) {
        document.body.style.height = "unset"
      }
    }

    setIsOpen(state)
  }

  if (!enabled) {
    return <>{children}</>
  }

  return (
    <SearchOverlayContext.Provider value={{ showOverlay, setShowOverlay }}>
      <div className={showOverlay && "sid-search-overlay__overlay"}>
        <div
          className={showOverlay && "sid-search-overlay__container"}
          ref={containerRef}
        >
          {showOverlay && (
            <a
              className="sid-search-overlay__close"
              onClick={() => setShowOverlay(false)}
            >
              Close Section
            </a>
          )}
          {children}
        </div>
      </div>
    </SearchOverlayContext.Provider>
  )
}
